import { ROLE_DATA } from '../data/roles.js';
import { DEFAULT_NIGHT_ROLE_ORDER, DEFAULT_SETTINGS } from '../data/constants.js';
import { buildNightQueue } from '../engine/buildNightQueue.js';
import { checkWinCondition } from '../engine/checkWinCondition.js';
import { killPlayer, generateId } from '../engine/utils.js';

export const initialState = {
  screen: "home",
  language: "fr",
  savedPlayers: [],
  activePlayerIds: [],
  roleConfig: { villagerCount: 3, werewolfCount: 2, specialRoles: [] },
  session: null,
  revealQueue: [],
  revealIndex: 0,
  revealShown: false,
  settings: { ...DEFAULT_SETTINGS },
  lastActivePlayerIds: [],
};

export function reducer(state, action) {
  switch (action.type) {

    case "SET_LANGUAGE":
      return { ...state, language: action.payload };

    case "LOAD_SETTINGS":
      return { ...state, settings: { ...state.settings, ...action.payload } };

    case "SET_SETTING": {
      const nextSettings = { ...state.settings, [action.payload.key]: action.payload.value };
      localStorage.setItem("ww_settings", JSON.stringify(nextSettings));
      return { ...state, settings: nextSettings };
    }

    case "LOAD_PLAYERS":
      return { ...state, savedPlayers: action.payload };

    case "LOAD_LAST_ACTIVE":
      return { ...state, lastActivePlayerIds: action.payload };

    case "ADD_PLAYER": {
      const p = { id: generateId(), name: action.payload };
      const saved = [...state.savedPlayers, p];
      localStorage.setItem("ww_players", JSON.stringify(saved));
      return { ...state, savedPlayers: saved };
    }

    case "REMOVE_PLAYER": {
      const saved = state.savedPlayers.filter(p => p.id !== action.payload);
      const active = state.activePlayerIds.filter(id => id !== action.payload);
      localStorage.setItem("ww_players", JSON.stringify(saved));
      return { ...state, savedPlayers: saved, activePlayerIds: active };
    }

    case "RENAME_PLAYER": {
      const saved = state.savedPlayers.map(p =>
        p.id === action.payload.id ? { ...p, name: action.payload.name } : p
      );
      localStorage.setItem("ww_players", JSON.stringify(saved));
      return { ...state, savedPlayers: saved };
    }

    case "REORDER_PLAYERS": {
      localStorage.setItem("ww_players", JSON.stringify(action.payload));
      return { ...state, savedPlayers: action.payload };
    }

    case "TOGGLE_PLAYER_ACTIVE": {
      const active = state.activePlayerIds.includes(action.payload)
        ? state.activePlayerIds.filter(id => id !== action.payload)
        : [...state.activePlayerIds, action.payload];
      return { ...state, activePlayerIds: active };
    }

    case "SELECT_ALL_PLAYERS":
      return { ...state, activePlayerIds: state.savedPlayers.map(p => p.id) };

    case "DESELECT_ALL_PLAYERS":
      return { ...state, activePlayerIds: [] };

    case "SET_VILLAGER_COUNT":
      return { ...state, roleConfig: { ...state.roleConfig, villagerCount: action.payload } };

    case "SET_WEREWOLF_COUNT":
      return { ...state, roleConfig: { ...state.roleConfig, werewolfCount: action.payload } };

    case "TOGGLE_SPECIAL_ROLE": {
      const rId = action.payload;
      const cur = state.roleConfig.specialRoles;
      const next = cur.includes(rId) ? cur.filter(r => r !== rId) : [...cur, rId];
      return { ...state, roleConfig: { ...state.roleConfig, specialRoles: next } };
    }

    case "GO_TO_HOME":
      return { ...state, screen: "home", session: null, activePlayerIds: [] };

    case "GO_TO_DOCS":
      return { ...state, screen: "docs" };

    case "GO_TO_SETUP": {
      const restore = state.activePlayerIds.length === 0 && state.lastActivePlayerIds.length > 0
        ? state.lastActivePlayerIds.filter(id => state.savedPlayers.some(p => p.id === id))
        : state.activePlayerIds;
      return { ...state, screen: "setup", activePlayerIds: restore };
    }

    case "START_REVEAL": {
      const ids = action.payload.session.players.map(p => p.id);
      localStorage.setItem("ww_last_active", JSON.stringify(ids));
      return {
        ...state,
        screen: "reveal",
        revealQueue: action.payload.queue,
        revealIndex: 0,
        revealShown: false,
        session: action.payload.session,
        lastActivePlayerIds: ids,
      };
    }

    case "SHOW_CARD":
      return { ...state, revealShown: true };

    case "NEXT_REVEAL": {
      const next = state.revealIndex + 1;
      if (next >= state.revealQueue.length) return { ...state, screen: "game", revealShown: false };
      return { ...state, revealIndex: next, revealShown: false };
    }

    case "ELECT_MAYOR": {
      if (!state.session) return state;
      const players = state.session.players.map(p => ({
        ...p,
        statuses: p.id === action.payload
          ? [...p.statuses.filter(s => s !== "mayor"), "mayor"]
          : p.statuses.filter(s => s !== "mayor"),
        mayor: p.id === action.payload,
      }));
      const extraDisabled = (state.session.sheepRemaining ?? 0) <= 0 ? ["shepherd"] : [];
      const queue = buildNightQueue(players, 1, state.session?.elderPenaltyActive || false,
        state.session.gameSettings?.nightRoleOrder || DEFAULT_NIGHT_ROLE_ORDER, extraDisabled);
      return {
        ...state,
        session: { ...state.session, players, mayorId: action.payload, phase: "night",
          roundNightActions: queue, currentNightRoleIndex: 0 },
      };
    }

    case "NEXT_NIGHT_ROLE": {
      if (!state.session) return state;
      const nextIdx = state.session.currentNightRoleIndex + 1;
      const phase = nextIdx >= state.session.roundNightActions.length ? "night_resolution" : "night";
      return { ...state, session: { ...state.session, currentNightRoleIndex: nextIdx, phase } };
    }

    case "SET_NIGHT_VICTIM":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, nightVictim: action.payload } };

    case "APPLY_PROTECTION":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, protector_lastProtected: action.payload } };

    case "LINK_LOVERS": {
      if (!state.session) return state;
      const [id1, id2] = action.payload;
      const players = state.session.players.map(p => {
        if (p.id === id1) return { ...p, statuses: [...p.statuses, "lover"], loverId: id2 };
        if (p.id === id2) return { ...p, statuses: [...p.statuses, "lover"], loverId: id1 };
        return p;
      });
      return { ...state, session: { ...state.session, players, cupidUsed: true } };
    }

    case "CHARM_PLAYERS": {
      if (!state.session) return state;
      const players = state.session.players.map(p =>
        action.payload.includes(p.id) && !p.statuses.includes("charmed")
          ? { ...p, statuses: [...p.statuses, "charmed"] }
          : p
      );
      return { ...state, session: { ...state.session, players } };
    }

    case "APPLY_WITCH_SAVE":
      if (!state.session) return state;
      return { ...state, session: {
        ...state.session,
        witch_usedSave: true,
        witch_savedVictim: true,
        nightLog: [
          ...(state.session.nightLog || []),
          { type: "witch_save", victimId: state.session.nightVictim },
        ],
      }};

    case "APPLY_WITCH_POISON":
      if (!state.session) return state;
      return { ...state, session: {
        ...state.session,
        pendingDeaths: [...state.session.pendingDeaths, action.payload],
        witch_usedPoison: true,
      }};

    case "APPLY_INFECTION":
      if (!state.session) return state;
      return { ...state, session: {
        ...state.session,
        infectionAttempted: true,
        fatherUsedInfection: true,
        nightLog: [
          ...(state.session.nightLog || []),
          { type: "infection", victimId: action.payload },
        ],
      }};

    case "SET_FOX_CHECK":
      // Stores the Fox's chosen target. Resolution (camp-smell check + ability
      // loss) happens in RESOLVE_NIGHT — no verdict is ever shown to the moderator.
      if (!state.session) return state;
      return { ...state, session: { ...state.session, fox_checkTargetId: action.payload } };

    case "GIVE_SHEEP": {
      // Shepherd gives one sheep from their remaining stock to a player.
      // Can be dispatched multiple times in the same night to different players.
      if (!state.session) return state;
      if ((state.session.sheepRemaining ?? 0) <= 0) return state;
      const players = state.session.players.map(p =>
        p.id === action.payload ? { ...p, sheepCount: (p.sheepCount || 0) + 1 } : p
      );
      return { ...state, session: {
        ...state.session,
        players,
        sheepRemaining: state.session.sheepRemaining - 1,
      }};
    }

    case "RESOLVE_NIGHT": {
      if (!state.session) return state;
      let players = [...state.session.players];
      const night = state.session.night;
      const newDeaths = [];
      const nightLog = [];
      const session = state.session;
      const isProtected = (id) => id === session.protector_lastProtected;
      const hasSheep = (id) => {
        const p = players.find(x => x.id === id);
        return (p?.sheepCount || 0) > 0;
      };
      const consumeSheep = (id) => {
        players = players.map(p => p.id === id ? { ...p, sheepCount: Math.max(0, (p.sheepCount || 0) - 1) } : p);
      };

      // ── 1. Bear Tamer check ──────────────────────────────────────
      const bearTamer = players.find(p => p.baseRoleId === "bear_tamer" && p.alive);
      if (bearTamer) {
        const aliveArr = players.filter(p => p.alive);
        const btIdx = aliveArr.findIndex(p => p.id === bearTamer.id);
        const n = aliveArr.length;
        const left  = aliveArr[(btIdx - 1 + n) % n];
        const right = aliveArr[(btIdx + 1) % n];
        const growls = [left, right].some(p => p.effectiveAlignment === "werewolf");
        nightLog.push({ type: growls ? "bear_growls" : "bear_calm" });
      }

      // ── 1b. Fox check — no verdict is ever exposed in the log ─────
      // The Fox smells camp alignment (not card identity) across the chosen
      // player and their two circular neighbors. If no werewolf-aligned
      // player is in that group of three, the Fox permanently loses its
      // power and becomes a Simple Villager. Otherwise, nothing changes —
      // the only signal is whether it wakes again next night.
      const foxTargetId = session.fox_checkTargetId;
      if (foxTargetId) {
        const fox = players.find(p => p.baseRoleId === "fox" && p.alive);
        const aliveArr = players.filter(p => p.alive);
        const fIdx = aliveArr.findIndex(p => p.id === foxTargetId);
        if (fox && fIdx !== -1) {
          const n = aliveArr.length;
          const left  = aliveArr[(fIdx - 1 + n) % n];
          const right = aliveArr[(fIdx + 1) % n];
          const group = [aliveArr[fIdx], left, right];
          const hasWolf = group.some(p => p.effectiveAlignment === "werewolf");
          if (!hasWolf) {
            players = players.map(p =>
              p.id === fox.id ? { ...p, baseRoleId: "villager", effectiveAlignment: "village" } : p
            );
            nightLog.push({ type: "fox_lost_power" });
          }
          // If a wolf IS present: ability is kept silently — no event, no message.
        }
      }

      // ── 2. Wolf attack — shield priority: Protector → Sheep → Elder → Death
      const vid = session.nightVictim;
      const victimProtected = vid ? isProtected(vid) : false;
      const victimHasSheep = vid ? (!victimProtected && hasSheep(vid)) : false;
      let elder_usedSurvival = session.elder_usedSurvival;
      let elderSurvivedThisNight = false;
      const infectionAttempted = session.infectionAttempted;
      let fatherUsedInfection = session.fatherUsedInfection;
      let infectionSucceeded = false;
      let victimShielded = victimProtected || victimHasSheep;

      // Determine Elder shield eligibility up front (3rd priority layer)
      if (vid && !victimShielded) {
        const target = players.find(p => p.id === vid);
        if (target?.alive && target.baseRoleId === "elder" && !elder_usedSurvival) {
          elderSurvivedThisNight = true; // will be finalized below alongside the log entry
        }
      }
      const fullyShielded = victimShielded || elderSurvivedThisNight;

      // Infection can only succeed if NO shield (protector, sheep, or elder) blocked the attack
      if (infectionAttempted && vid && !fullyShielded) {
        const target = players.find(p => p.id === vid && p.alive);
        if (target) {
          players = players.map(p =>
            p.id === vid
              ? { ...p, effectiveAlignment: "werewolf", statuses: [...p.statuses, "infected"] }
              : p
          );
          infectionSucceeded = true;
        }
      }

      if (vid) {
        const victim = players.find(p => p.id === vid);
        if (victimProtected) {
          nightLog.push({ type: "wolf_protected", victimId: vid });
        } else if (victimHasSheep) {
          consumeSheep(vid);
          nightLog.push({ type: "sheep_protected", victimId: vid });
        } else if (elderSurvivedThisNight) {
          elder_usedSurvival = true;
          nightLog.push({ type: "elder_extra_life", victimId: vid });
        } else if (infectionSucceeded) {
          // Victim survived via infection — no kill
        } else if (victim?.alive) {
          players = killPlayer(players, vid, night);
          newDeaths.push(vid);
          nightLog.push({ type: "wolf_kill", victimId: vid });
          const loverOfVictim = players.find(p => p.id === victim.loverId && !p.alive && p.deathNight === night);
          if (loverOfVictim) {
            newDeaths.push(loverOfVictim.id);
            nightLog.push({ type: "lover_death", victimId: loverOfVictim.id, partnerId: vid });
          }
        }
      }

      // ── 3. Infection post-check — ability not consumed if any shield blocked it
      if (infectionAttempted && vid && fullyShielded) {
        fatherUsedInfection = false;
        nightLog.push({ type: "infection_failed_protected", victimId: vid });
      }

      // ── 4. Witch save resolution ─────────────────────────────────
      let witch_usedSave = session.witch_usedSave;
      if (session.witch_savedVictim && vid) {
        if (fullyShielded) {
          witch_usedSave = false;
          nightLog.push({ type: "witch_save_wasted", victimId: vid });
        } else if (newDeaths.includes(vid)) {
          players = players.map(p => p.id === vid ? { ...p, alive: true, deathNight: null } : p);
          const idx = newDeaths.indexOf(vid);
          newDeaths.splice(idx, 1);
          nightLog.push({ type: "witch_save_success", victimId: vid });
        }
      }

      // ── 5. BBW extra victim — Protector + Sheep shields apply (matches Protector's existing scope)
      if (session.bbw_extraVictimId) {
        const bbwId = session.bbw_extraVictimId;
        const bbwTarget = players.find(p => p.id === bbwId);
        const bbwProtected = isProtected(bbwId);
        const bbwSheep = !bbwProtected && hasSheep(bbwId);
        if (bbwTarget?.alive && !bbwProtected && !bbwSheep) {
          players = killPlayer(players, bbwId, night);
          newDeaths.push(bbwId);
          nightLog.push({ type: "bbw_kill", victimId: bbwId });
          const loverOfBbw = players.find(p => p.id === bbwTarget.loverId && !p.alive && p.deathNight === night);
          if (loverOfBbw && !newDeaths.includes(loverOfBbw.id)) {
            newDeaths.push(loverOfBbw.id);
            nightLog.push({ type: "lover_death", victimId: loverOfBbw.id, partnerId: bbwId });
          }
        } else if (bbwTarget && bbwProtected) {
          nightLog.push({ type: "wolf_protected", victimId: bbwId });
        } else if (bbwTarget && bbwSheep) {
          consumeSheep(bbwId);
          nightLog.push({ type: "sheep_protected", victimId: bbwId });
        }
      }

      // ── 5b. White Werewolf kill — Protector + Sheep shields apply ─
      if (session.ww_killTargetId) {
        const wwId = session.ww_killTargetId;
        const wwTarget = players.find(p => p.id === wwId);
        const wwProtected = isProtected(wwId);
        const wwSheep = !wwProtected && hasSheep(wwId);
        if (wwTarget?.alive && !wwProtected && !wwSheep) {
          players = killPlayer(players, wwId, night);
          newDeaths.push(wwId);
          nightLog.push({ type: "ww_kill", victimId: wwId });
          const loverOfWw = players.find(p => p.id === wwTarget.loverId && !p.alive && p.deathNight === night);
          if (loverOfWw && !newDeaths.includes(loverOfWw.id)) {
            newDeaths.push(loverOfWw.id);
            nightLog.push({ type: "lover_death", victimId: loverOfWw.id, partnerId: wwId });
          }
        } else if (wwTarget && wwSheep) {
          consumeSheep(wwId);
          nightLog.push({ type: "sheep_protected", victimId: wwId });
        }
      }

      // ── 6. Witch poison ──────────────────────────────────────────
      for (const deadId of session.pendingDeaths) {
        const p = players.find(x => x.id === deadId);
        if (p?.alive) {
          players = killPlayer(players, deadId, night);
          newDeaths.push(deadId);
          nightLog.push({ type: "witch_poison", victimId: deadId });
          const loverOfPoisoned = players.find(lp => lp.id === p.loverId && !lp.alive && lp.deathNight === night);
          if (loverOfPoisoned && !newDeaths.includes(loverOfPoisoned.id)) {
            newDeaths.push(loverOfPoisoned.id);
            nightLog.push({ type: "lover_death", victimId: loverOfPoisoned.id, partnerId: deadId });
          }
        }
      }

      // ── 7. Wild Child role model death check ─────────────────────
      const wildChild = players.find(p => p.baseRoleId === "wild_child");
      const roleModelId = session.wildChild_roleModelId;
      if (wildChild?.alive && roleModelId && newDeaths.includes(roleModelId)
          && wildChild.effectiveAlignment !== "werewolf") {
        players = players.map(p =>
          p.id === wildChild.id ? { ...p, effectiveAlignment: "werewolf" } : p
        );
        nightLog.push({ type: "wild_child_turned", wildChildId: wildChild.id, modelId: roleModelId });
      }

      // ── 8. Elder penalty ─────────────────────────────────────────
      let elderPenaltyActive = session.elderPenaltyActive;
      const elderPlayer = players.find(p => p.baseRoleId === "elder");
      if (elderPlayer && !elderPlayer.alive && !elderPenaltyActive && newDeaths.includes(elderPlayer.id)) {
        elderPenaltyActive = true;
        nightLog.push({ type: "elder_penalty" });
      }

      // ── 9. Peaceful night check ──────────────────────────────────
      const meaningfulEvents = nightLog.filter(e =>
        !["bear_growls","bear_calm","elder_extra_life","fox_lost_power"].includes(e.type)
      );
      const priorLog = session.nightLog || [];
      const hasActivityInPrior = priorLog.some(e =>
        ["witch_save","witch_save_success","infection"].includes(e.type)
      );
      if (meaningfulEvents.length === 0 && !hasActivityInPrior) {
        nightLog.push({ type: "peaceful_night" });
      }
      const combinedLog = [...priorLog, ...nightLog];

      // ── 10. Clear per-night statuses ─────────────────────────────
      players = players.map(p => ({
        ...p,
        statuses: p.statuses.filter(s => s !== "protected"),
        voteCount: 0,
      }));

      // ── 11. Hunter check ─────────────────────────────────────────
      const hunterDead = newDeaths.some(id => {
        const p = players.find(x => x.id === id);
        return p?.baseRoleId === "hunter";
      });

      const winner = checkWinCondition(players);

      return { ...state, session: {
        ...state.session,
        players,
        fatherUsedInfection,
        witch_usedSave,
        witch_savedVictim: false,
        infectionAttempted: false,
        elder_usedSurvival,
        elderPenaltyActive,
        phase: winner ? "game_over" : (hunterDead ? "hunter_shot" : "day_reveal"),
        dayRevealSource: "night",
        day: session.day + 1,
        nightVictim: null,
        pendingDeaths: [],
        bbw_extraVictimId: null,
        ww_killTargetId: null,
        fox_checkTargetId: null,
        ravenMarkId: session.ravenMarkId,
        newDeaths,
        nightLog: combinedLog,
        winner,
        hunterPendingShot: hunterDead,
      }};
    }

    case "START_NIGHT": {
      if (!state.session) return state;
      const newNight = state.session.night + 1;
      const extraDisabled = (state.session.sheepRemaining ?? 0) <= 0 ? ["shepherd"] : [];
      const queue = buildNightQueue(state.session.players, newNight,
        state.session.elderPenaltyActive || false,
        state.session.gameSettings?.nightRoleOrder || DEFAULT_NIGHT_ROLE_ORDER,
        extraDisabled);
      const playersCleared = state.session.players.map(p => ({
        ...p, statuses: p.statuses.filter(s => s !== "raven_mark"),
      }));
      return { ...state, session: {
        ...state.session,
        players: playersCleared,
        phase: "night",
        night: newNight,
        roundNightActions: queue,
        currentNightRoleIndex: 0,
        newDeaths: [],
        nightLog: [],
        nightVictim: null,
        pendingDeaths: [],
        infectionAttempted: false,
        witch_savedVictim: false,
        ravenMarkId: null,
        fox_checkTargetId: null,
      }};
    }

    case "START_DEBATE":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, phase: "debate", currentDebateSpeaker: 0 } };

    case "SKIP_TO_VOTE": {
      if (!state.session) return state;
      const ravenId = state.session.ravenMarkId;
      const players = ravenId
        ? state.session.players.map(p =>
            p.id === ravenId && p.alive ? { ...p, voteCount: (p.voteCount || 0) + 2 } : p
          )
        : state.session.players;
      return { ...state, session: { ...state.session, phase: "voting", players } };
    }

    case "NEXT_DEBATE_SPEAKER": {
      if (!state.session) return state;
      const alive = state.session.players.filter(p => p.alive);
      const next = state.session.currentDebateSpeaker + 1;
      if (next >= alive.length) {
        const ravenId = state.session.ravenMarkId;
        const players = ravenId
          ? state.session.players.map(p =>
              p.id === ravenId && p.alive ? { ...p, voteCount: (p.voteCount || 0) + 2 } : p
            )
          : state.session.players;
        return { ...state, session: { ...state.session, phase: "voting", players } };
      }
      return { ...state, session: { ...state.session, currentDebateSpeaker: next } };
    }

    case "ADD_VOTE": {
      if (!state.session) return state;
      const players = state.session.players.map(p =>
        p.id === action.payload ? { ...p, voteCount: p.voteCount + 1 } : p
      );
      return { ...state, session: { ...state.session, players } };
    }

    case "REMOVE_VOTE": {
      if (!state.session) return state;
      const players = state.session.players.map(p =>
        p.id === action.payload && p.voteCount > 0 ? { ...p, voteCount: p.voteCount - 1 } : p
      );
      return { ...state, session: { ...state.session, players } };
    }

    case "RESET_VOTES": {
      if (!state.session) return state;
      const players = state.session.players.map(p => ({ ...p, voteCount: 0 }));
      return { ...state, session: { ...state.session, players } };
    }

    case "REVOTE_TIED": {
      if (!state.session) return state;
      const players = state.session.players.map(p => ({ ...p, voteCount: 0 }));
      return { ...state, session: { ...state.session, players, revoteTiedIds: action.payload } };
    }

    case "CLEAR_REVOTE": {
      if (!state.session) return state;
      return { ...state, session: { ...state.session, revoteTiedIds: null } };
    }

    case "EXECUTE_PLAYER": {
      if (!state.session) return state;
      let players = killPlayer(state.session.players, action.payload, state.session.night - 1);
      players = players.map(p => ({ ...p, voteCount: 0, statuses: p.statuses.filter(s => s !== "raven_mark") }));
      const executedPlayer = state.session.players.find(p => p.id === action.payload);
      const executedIsHunter = executedPlayer?.baseRoleId === "hunter";
      const execLog = [{ type: "execution", victimId: action.payload }];
      if (executedPlayer?.loverId) {
        const lover = players.find(p => p.id === executedPlayer.loverId && !p.alive);
        if (lover) execLog.push({ type: "lover_death", victimId: lover.id, partnerId: action.payload });
      }
      const isAngel = executedPlayer?.baseRoleId === "angel";
      const isDay1 = state.session.day === 1;
      if (isAngel && isDay1) {
        return { ...state, session: {
          ...state.session, players,
          phase: "game_over", dayRevealSource: "execution",
          winner: "angel", newDeaths: [action.payload],
          nightLog: execLog, hunterPendingShot: false, revoteTiedIds: null,
        }};
      }
      const wildChild = players.find(p => p.baseRoleId === "wild_child" && p.alive);
      const roleModelId = state.session.wildChild_roleModelId;
      let elderPenaltyActive = state.session.elderPenaltyActive;
      if (wildChild && roleModelId === action.payload && wildChild.effectiveAlignment !== "werewolf") {
        players = players.map(p =>
          p.id === wildChild.id ? { ...p, effectiveAlignment: "werewolf" } : p
        );
        execLog.push({ type: "wild_child_turned", wildChildId: wildChild.id, modelId: action.payload });
      }
      const elderPlayer = players.find(p => p.baseRoleId === "elder" && !p.alive && p.id === action.payload);
      if (elderPlayer && !elderPenaltyActive) {
        elderPenaltyActive = true;
        execLog.push({ type: "elder_penalty" });
      }
      const winner = checkWinCondition(players);
      return { ...state, session: {
        ...state.session, players, elderPenaltyActive,
        phase: winner ? "game_over" : (executedIsHunter ? "hunter_shot" : "day_reveal"),
        dayRevealSource: "execution", winner,
        newDeaths: [action.payload, ...execLog.filter(e => e.type === "lover_death").map(e => e.victimId)],
        nightLog: execLog, hunterPendingShot: executedIsHunter, revoteTiedIds: null, ravenMarkId: null,
      }};
    }

    case "HUNTER_SHOOT": {
      if (!state.session) return state;
      let players = killPlayer(state.session.players, action.payload, state.session.night - 1);
      const huntLog = [...(state.session.nightLog || []), { type: "hunter_shot", victimId: action.payload }];
      const shot = state.session.players.find(p => p.id === action.payload);
      if (shot?.loverId) {
        const lover = players.find(p => p.id === shot.loverId && !p.alive);
        if (lover) huntLog.push({ type: "lover_death", victimId: lover.id, partnerId: action.payload });
      }
      const winner = checkWinCondition(players);
      const prevSource = state.session.dayRevealSource || "night";
      return { ...state, session: {
        ...state.session, players,
        phase: winner ? "game_over" : "day_reveal",
        dayRevealSource: prevSource, winner, hunterPendingShot: false,
        nightLog: huntLog, newDeaths: [...(state.session.newDeaths || []), action.payload],
      }};
    }

    case "APPLY_RAVEN_MARK": {
      if (!state.session) return state;
      const players = state.session.players.map(p => ({
        ...p,
        statuses: p.id === action.payload
          ? [...p.statuses.filter(s => s !== "raven_mark"), "raven_mark"]
          : p.statuses.filter(s => s !== "raven_mark"),
      }));
      return { ...state, session: { ...state.session, players, ravenMarkId: action.payload } };
    }

    case "STEAL_ROLE": {
      if (!state.session) return state;
      const { thiefId, targetId } = action.payload;
      const target = state.session.players.find(p => p.id === targetId);
      if (!target) return state;
      const stolenRole = ROLE_DATA[target.baseRoleId];
      const players = state.session.players.map(p => {
        if (p.id === thiefId) return { ...p, baseRoleId: target.baseRoleId, effectiveAlignment: stolenRole.alignment };
        if (p.id === targetId) return { ...p, baseRoleId: "villager", effectiveAlignment: "village" };
        return p;
      });
      return { ...state, session: { ...state.session, players } };
    }

    case "SET_WILD_CHILD_MODEL":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, wildChild_roleModelId: action.payload } };

    case "APPLY_BBW_KILL":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, bbw_extraVictimId: action.payload } };

    case "APPLY_WW_KILL":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, ww_killTargetId: action.payload } };

    case "GAME_OVER":
      if (!state.session) return state;
      return { ...state, session: { ...state.session, phase: "game_over", winner: action.payload } };

    default:
      return state;
  }
}
